import React, { useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import ReactQuill from 'react-quill';
import { Filter } from 'bad-words';
import 'react-quill/dist/quill.snow.css';

const AppRichTextEditor = ({
  value = '',
  onChange = () => {},
  placeholder = 'Enter text...',
  minHeight = '120px',
  error = null,
  touched = false,
  label = null,
  modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ align: [] }],
      ['clean']
    ]
  },
  formats = ['header', 'bold', 'italic', 'underline', 'list', 'bullet', 'align'],
  disabled = false,
  ...props
}) => {
  const filter = new Filter();

  // Callback to sanitize inappropriate words
  const handleChange = useCallback(
  (content) => {
    try {
      // Normalize Quill's empty output
      const normalized = content.replace(/<p><br><\/p>/g, "").replace(/<p>\s*<\/p>/g, "").trim();

      if (!normalized) {
        onChange(""); // Treat as truly empty
        return;
      }

      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = normalized;

      const plainText = tempDiv.textContent || "";
      const filteredText = filter.clean(plainText.trim());

      if (filteredText === plainText.trim()) {
        onChange(normalized);
        return;
      }

      const words = plainText.trim().split(/\s+/);
      const filteredWords = filteredText.split(/\s+/);
      let sanitizedContent = normalized;

      words.forEach((word, index) => {
        if (word && filteredWords[index] && word !== filteredWords[index]) {
          const regex = new RegExp(`(>|^)([^<]*?)(${word})([^>]*?)(<|$)`, "g");
          sanitizedContent = sanitizedContent.replace(regex, `$1$2${filteredWords[index]}$4$5`);
        }
      });

      onChange(sanitizedContent);
    } catch (error) {
      console.error("Error filtering content:", error);
      onChange(content);
    }
  },
  [onChange]
);

  return (
    <Box>
      {label && (
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {label}
        </Typography>
      )}
      <Box
        sx={{
          '& .ql-container': { minHeight },
          '& .ql-editor': { minHeight }
        }}
      >
        <ReactQuill
          theme="snow"
          value={value}
          onChange={handleChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          readOnly={disabled}
          {...props}
        />
      </Box>
      {touched && error && (
        <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default AppRichTextEditor;